import { type PublicProfile, users } from '@/lib/api';
import { useDateLocale } from '@/lib/hooks/use-date-locale';
import { routes } from '@/lib/routes';
import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, Calendar, Loader2, MapPin, PenLine } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Link } from 'react-router';

export default function UserProfilePage() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const params = useParams();
  const username = params.username as string;

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await users.getPublicProfile(username);
        setProfile(data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-medium text-ink mb-2">{t('common.error')}</h2>
        <p className="text-sepia/70 mb-6">{t('common.error')}</p>
        <Link
          to={routes.discover}
          className="inline-flex items-center gap-2 text-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      <Link
        to={routes.discover}
        className="inline-flex items-center gap-2 text-sepia hover:text-ink mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </Link>

      {/* Profile Card */}
      <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-20 h-20 rounded-full bg-gray-warm/50 flex items-center justify-center text-3xl font-medium text-sepia">
            {profile.username.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-serif text-ink">{profile.username}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-sepia/60">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {profile.country}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {t('user.memberSince', {
                  date: formatDistanceToNow(new Date(profile.createdAt), {
                    addSuffix: true,
                    locale: dateLocale
                  })
                })}
              </span>
            </div>
          </div>

          {/* Write button */}
          <Link
            to={routes.composeToUser(profile.id)}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all"
            style={{ boxShadow: 'var(--shadow-sm)' }}
          >
            <PenLine className="w-4 h-4" />
            {t('user.sendLetter')}
          </Link>
        </div>

        {/* Bio */}
        {profile.bio ? (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-sepia mb-2">{t('user.bio')}</h3>
            <p className="text-ink leading-relaxed font-serif">{profile.bio}</p>
          </div>
        ) : (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-sepia mb-2">{t('user.bio')}</h3>
            <p className="text-sepia/60 italic">{t('user.noBio')}</p>
          </div>
        )}

        {/* Interests */}
        {(profile.interests?.length ?? 0) > 0 ? (
          <div>
            <h3 className="text-sm font-medium text-sepia mb-3">{t('user.interests')}</h3>
            <div className="flex flex-wrap gap-2">
              {(profile.interests ?? []).map((interest) => (
                <span
                  key={interest.id}
                  className="px-3 py-1.5 bg-gray-warm/30 text-sepia rounded-full text-sm font-medium"
                >
                  {interest.name}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-sm font-medium text-sepia mb-3">{t('user.interests')}</h3>
            <p className="text-sepia/60 italic">{t('user.noInterests')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
